import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useMerchantAuthUser } from "../auth/MerchantAuthContext";
import MerchantDatePickerField from "../components/MerchantDatePickerField";
import MerchantStatCard from "../components/MerchantStatCard";
import {
  fetchMerchantSettlements,
  getMerchantApiErrorMessage,
  resolveMerchantPortalUrl,
} from "../data/merchantApi";
import { formatMerchantCurrency } from "../data/merchantData";
import {
  pickMerchantField,
  toMerchantNumber,
  toMerchantString,
} from "../data/merchantLegacyUtils";

const SETTLEMENTS_PAGE_SIZE = 31;
const SETTLEMENT_MONTH_OPTION_COUNT = 36;

function getCurrentSettlementMonth() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");

  return String(now.getFullYear()) + "-" + month;
}

function getSettlementMonthDate(monthValue) {
  const matched = toMerchantString(monthValue).match(/^(\d{4})-(\d{2})$/);

  if (!matched) {
    return null;
  }

  return new Date(Number(matched[1]), Number(matched[2]) - 1, 1);
}

function getSettlementMonthValueFromDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }

  return (
    String(date.getFullYear()) +
    "-" +
    String(date.getMonth() + 1).padStart(2, "0")
  );
}

function getEarliestSettlementMonthDate() {
  const now = new Date();

  return new Date(
    now.getFullYear(),
    now.getMonth() - SETTLEMENT_MONTH_OPTION_COUNT + 1,
    1,
  );
}

function getLatestSettlementMonthDate() {
  const now = new Date();

  return new Date(now.getFullYear(), now.getMonth(), 1);
}

function getSettlementMonthFromSearch(search) {
  const searchParams = new URLSearchParams(search || "");
  const month = toMerchantString(searchParams.get("month"));

  return /^\d{4}-\d{2}$/.test(month) ? month : "";
}

function formatSettlementMonthLabel(monthValue) {
  const normalizedMonth = toMerchantString(monthValue);
  const matched = normalizedMonth.match(/^(\d{4})-(\d{2})$/);

  if (!matched) {
    return normalizedMonth || "Current Month";
  }

  const monthDate = new Date(Date.UTC(Number(matched[1]), Number(matched[2]) - 1, 1));

  return monthDate.toLocaleString("en-SG", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatSettlementShortMonthLabel(monthValue) {
  const normalizedMonth = toMerchantString(monthValue);
  const matched = normalizedMonth.match(/^(\d{4})-(\d{2})$/);

  if (!matched) {
    return normalizedMonth || "Current";
  }

  const monthDate = new Date(Date.UTC(Number(matched[1]), Number(matched[2]) - 1, 1));

  return monthDate.toLocaleString("en-SG", {
    month: "short",
    timeZone: "UTC",
  });
}

function formatSettlementDisplayDate(value) {
  const normalizedValue = toMerchantString(value);
  const matchedDate = normalizedValue.match(/\d{4}-\d{2}-\d{2}/);

  if (!normalizedValue) {
    return "—";
  }

  if (matchedDate && matchedDate[0]) {
    return matchedDate[0];
  }

  const parsedDate = new Date(normalizedValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return normalizedValue;
  }

  return parsedDate.toISOString().slice(0, 10);
}

function normalizeSettlementItem(item) {
  const summaryId = toMerchantString(pickMerchantField(item, ["id"]), "");

  return {
    id: toMerchantString(pickMerchantField(item, ["id", "settlement_record_id"]), ""),
    summaryId: summaryId,
    batch: toMerchantString(pickMerchantField(item, ["batch_id"]), "—"),
    date: formatSettlementDisplayDate(
      pickMerchantField(item, ["settlement_time", "date", "created_at"]),
    ),
    gross: toMerchantNumber(pickMerchantField(item, ["gross_amount"], 0)),
    refunds: toMerchantNumber(pickMerchantField(item, ["refund_amount"], 0)),
    fees: toMerchantNumber(pickMerchantField(item, ["fees_amount"], 0)),
    net: toMerchantNumber(pickMerchantField(item, ["net_payout_amount"], 0)),
    status: toMerchantString(pickMerchantField(item, ["status"]), ""),
    reportUrl: resolveMerchantPortalUrl(
      pickMerchantField(item, ["report_url", "report_file_path"], ""),
    ),
  };
}

function createInitialState(period) {
  const currentMonth = period || getCurrentSettlementMonth();

  return {
    period: currentMonth,
    rows: [],
    totalNet: 0,
    totalFees: 0,
    batchCount: 0,
    bankName: "Pending backend data",
    bankAccount: "—",
    accountName: "Merchant account",
    total: 0,
  };
}

function normalizeSettlementsResponse(response, authUser, fallbackPeriod) {
  const summary = response && response.summary ? response.summary : {};
  const settlementBank = response && response.settlement_bank ? response.settlement_bank : {};
  const rows = Array.isArray(response && response.items)
    ? response.items.map(normalizeSettlementItem)
    : [];
  const bankAccount = toMerchantString(
    pickMerchantField(settlementBank, ["bank_account_masked", "bank_account"]),
    "—",
  );

  return {
    period: toMerchantString(
      pickMerchantField(response, ["period"]),
      fallbackPeriod || getCurrentSettlementMonth(),
    ),
    rows: rows,
    totalNet: toMerchantNumber(
      pickMerchantField(summary, ["total_settled_amount"], 0),
    ),
    totalFees: toMerchantNumber(
      pickMerchantField(summary, ["total_fees_amount"], 0),
    ),
    batchCount: toMerchantNumber(
      pickMerchantField(summary, ["batch_count"], rows.length),
    ),
    bankName: toMerchantString(
      pickMerchantField(settlementBank, ["bank_name"]),
      "Pending backend data",
    ),
    bankAccount: bankAccount,
    accountName: toMerchantString(
      pickMerchantField(settlementBank, ["account_holder_name"]),
      toMerchantString(pickMerchantField(authUser, ["businessName", "name"]), "Merchant account"),
    ),
    total: toMerchantNumber(pickMerchantField(response, ["total"], rows.length)),
  };
}

export default function MerchantSettlementsPage() {
  const history = useHistory();
  const location = useLocation();
  const authUser = useMerchantAuthUser();
  const initialMonthFromQuery = getSettlementMonthFromSearch(location.search);
  const [selectedMonth, setSelectedMonth] = React.useState(
    initialMonthFromQuery || getCurrentSettlementMonth(),
  );
  const [requestState, setRequestState] = React.useState("loading");
  const [feedbackMessage, setFeedbackMessage] = React.useState("");
  const [state, setState] = React.useState(function getInitialState() {
    return createInitialState(selectedMonth);
  });

  React.useEffect(
    function syncSelectedMonthFromUrl() {
      const nextMonthFromQuery = getSettlementMonthFromSearch(location.search);
      const nextMonth = nextMonthFromQuery || getCurrentSettlementMonth();

      setSelectedMonth(function updateSelectedMonth(previousMonth) {
        return previousMonth === nextMonth ? previousMonth : nextMonth;
      });
    },
    [location.search],
  );

  React.useEffect(
    function loadMerchantSettlements() {
      let isActive = true;

      async function requestSettlements() {
        setRequestState("loading");
        setFeedbackMessage("");

        try {
          const response = await fetchMerchantSettlements({
            month: selectedMonth,
            page: 1,
            page_size: SETTLEMENTS_PAGE_SIZE,
          });

          if (!isActive) {
            return;
          }

          setState(normalizeSettlementsResponse(response, authUser, selectedMonth));
          setRequestState("success");
        } catch (error) {
          if (!isActive) {
            return;
          }

          setState(createInitialState(selectedMonth));
          setRequestState("error");
          setFeedbackMessage(
            getMerchantApiErrorMessage(
              error,
              "Settlements could not be loaded from the merchant API.",
            ),
          );
        }
      }

      requestSettlements();

      return function cleanupMerchantSettlements() {
        isActive = false;
      };
    },
    [authUser, selectedMonth],
  );

  const subtitle =
    "T+1 settlement batches · " + formatSettlementMonthLabel(state.period);
  const bankDisplay = state.bankAccount !== "—"
    ? state.bankName + " " + state.bankAccount
    : state.bankName;

  function openSettlementReport(summaryId) {
    if (!summaryId || typeof window === "undefined") {
      return;
    }

    const reportUrl =
      window.location.origin +
      window.location.pathname +
      "#/settlements/" +
      encodeURIComponent(summaryId) +
      "/report";

    window.open(reportUrl, "_blank", "noopener,noreferrer");
  }

  function handleMonthChange(date) {
    const nextMonth = getSettlementMonthValueFromDate(date);

    if (!nextMonth || nextMonth === selectedMonth) {
      return;
    }

    const searchParams = new URLSearchParams(location.search || "");
    searchParams.set("month", nextMonth);
    searchParams.delete("page");
    searchParams.delete("page_size");

    setSelectedMonth(nextMonth);
    history.push({
      pathname: location.pathname,
      search: "?" + searchParams.toString(),
    });
  }

  return (
    <div className="merchant-page">
      <div className="merchant-page-header merchant-page-header-row">
        <div>
          <div className="merchant-page-title">Settlements</div>
          <div className="merchant-page-subtitle">{subtitle}</div>
        </div>
        <div className="merchant-page-actions">
          <MerchantDatePickerField
            mode="month"
            minDate={getEarliestSettlementMonthDate()}
            maxDate={getLatestSettlementMonthDate()}
            placeholder="Select month"
            selected={getSettlementMonthDate(selectedMonth)}
            onChange={handleMonthChange}
          />
        </div>
      </div>

      {requestState === "loading" || feedbackMessage ? (
        <div className="merchant-data-note">
          {requestState === "loading"
            ? "Loading live settlements from the merchant API."
            : feedbackMessage}
          {requestState === "loading" ? (
            <div className="merchant-inline-status merchant-inline-status-loading">
              <span className="merchant-request-spinner" />
              Request in progress
            </div>
          ) : null}
          {requestState === "success" && feedbackMessage ? (
            <div className="merchant-inline-status">{feedbackMessage}</div>
          ) : null}
        </div>
      ) : null}

      <div className="merchant-compact-grid">
        <MerchantStatCard
          stripeClassName="merchant-stripe-green"
          label={"Total Settled (" + formatSettlementShortMonthLabel(state.period) + ")"}
          value={formatMerchantCurrency(state.totalNet)}
        >
          {state.batchCount.toLocaleString("en-SG")} batches completed
        </MerchantStatCard>
        <MerchantStatCard
          stripeClassName="merchant-stripe-red"
          label="Total Fees Deducted"
          value={formatMerchantCurrency(state.totalFees)}
        >
          {state.total.toLocaleString("en-SG")} records returned
        </MerchantStatCard>
        <MerchantStatCard
          stripeClassName="merchant-stripe-blue"
          label="Settlement Bank"
          value={bankDisplay}
        >
          {state.accountName}
        </MerchantStatCard>
      </div>

      <div className="merchant-mobile-settlement-list">
        {state.rows.length ? (
          state.rows.map(function renderSettlementCard(settlement) {
            return (
              <div
                className="merchant-mobile-settlement-card"
                key={(settlement.id || settlement.batch) + "-mobile"}
              >
                <div className="merchant-mobile-settlement-top">
                  <div>
                    <div className="merchant-mobile-settlement-date">{settlement.date}</div>
                    <div className="merchant-mobile-settlement-batch merchant-mono">
                      {settlement.batch}
                    </div>
                  </div>
                  <button
                    className="merchant-button merchant-button-ghost"
                    type="button"
                    disabled={!settlement.summaryId}
                    onClick={function handleOpenReport() {
                      openSettlementReport(settlement.summaryId);
                    }}
                  >
                    PDF
                  </button>
                </div>
                <div className="merchant-mobile-settlement-bottom">
                  <div>
                    <div className="merchant-mobile-settlement-label">Net payout</div>
                    <div className="merchant-mobile-settlement-net merchant-mono">
                      {formatMerchantCurrency(settlement.net)}
                    </div>
                  </div>
                  <div className="merchant-mobile-settlement-fees merchant-mono">
                    −{formatMerchantCurrency(settlement.fees)}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="merchant-mobile-settlement-card merchant-card-empty">
            {requestState === "loading"
              ? "Loading settlements..."
              : "No settlements available for " + formatSettlementMonthLabel(state.period) + "."}
          </div>
        )}
      </div>

      <div className="merchant-card merchant-card-table merchant-desktop-settlement-table">
        <div className="merchant-settlement-head">
          <span>Date</span>
          <span>Batch ID</span>
          <span>Gross (SGD)</span>
          <span>Fees</span>
          <span>Net Payout</span>
          <span>Report</span>
        </div>
        {state.rows.length ? (
          state.rows.map(function renderSettlement(settlement) {
            return (
              <div
                className="merchant-settlement-row"
                key={settlement.id || settlement.batch}
              >
                <span>{settlement.date}</span>
                <span className="merchant-mono">{settlement.batch}</span>
                <span className="merchant-mono">
                  {formatMerchantCurrency(settlement.gross)}
                </span>
                <span className="merchant-mono merchant-negative">
                  −{formatMerchantCurrency(settlement.fees)}
                </span>
                <span className="merchant-mono merchant-positive">
                  {formatMerchantCurrency(settlement.net)}
                </span>
                <button
                  className="merchant-button merchant-button-ghost"
                  type="button"
                  disabled={!settlement.summaryId}
                  onClick={function handleOpenReport() {
                    openSettlementReport(settlement.summaryId);
                  }}
                >
                  PDF
                </button>
              </div>
            );
          })
        ) : (
          <div className="merchant-card-empty">
            {requestState === "loading"
              ? "Loading settlements..."
              : "No settlements available for " + formatSettlementMonthLabel(state.period) + "."}
          </div>
        )}
      </div>
    </div>
  );
}
